import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography
} from 'antd'
import { money, validateRefund } from './utils'

const { TextArea } = Input

// The five things our team can do to an order. Two of them spend the owner's money — retrying a
// report buys it again, and switching the package buys a different one at no charge to the
// customer — so both say so before they are confirmed.

export function NoteModal({ open, saving, onCancel, onSubmit }) {
  const [note, setNote] = useState('')
  return (
    <Modal
      title="Add a note"
      visible={open}
      confirmLoading={saving}
      okButtonProps={{ disabled: !note.trim() }}
      onOk={() => onSubmit({ kind: 'note', note: note.trim() })}
      onCancel={onCancel}
    >
      <TextArea
        rows={4}
        value={note}
        onChange={event => setNote(event.target.value)}
        placeholder="What happened, in the words the next person needs."
      />
    </Modal>
  )
}

NoteModal.propTypes = {
  open: PropTypes.bool,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

export function CloseModal({ open, saving, onCancel, onSubmit }) {
  const [note, setNote] = useState('')
  return (
    <Modal
      title="Close this order"
      visible={open}
      confirmLoading={saving}
      okText="Close it"
      onOk={() => onSubmit({ kind: 'close', note: note.trim() })}
      onCancel={onCancel}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Nothing further will happen on its own."
        description="Any report still being fetched is cancelled and every open alert is cleared. Closing it is the resolution."
      />
      <TextArea
        rows={3}
        value={note}
        onChange={event => setNote(event.target.value)}
        placeholder="Why it is being closed."
      />
    </Modal>
  )
}

CloseModal.propTypes = {
  open: PropTypes.bool,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

// Prefilled with the server's suggested amount, because it already knows the rule: the full price
// when nothing came back, and the share that produced nothing when only part did.
export function RefundModal({ open, saving, order, onCancel, onSubmit }) {
  const suggested = (order || {}).suggestedRefund || {}
  const [amount, setAmount] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [reference, setReference] = useState('')
  const [touched, setTouched] = useState(false)

  React.useEffect(() => {
    if (!open) return
    setAmount(suggested.amount ? Number(suggested.amount) : null)
    setCurrency(suggested.currency || (order || {}).priceCurrency || 'GHS')
    setReference('')
    setTouched(false)
    // Deliberately keyed on `open` alone: the form resets when the modal opens and never
    // while the operator is typing in it.
  }, [open]) // eslint-disable-line

  const values = { amount, currency, reference, maxAmount: (order || {}).priceAmount }
  const errors = validateRefund(values)
  const valid = Object.keys(errors).length === 0

  return (
    <Modal
      title="Record a refund"
      visible={open}
      confirmLoading={saving}
      okText="Record it"
      okButtonProps={{ disabled: !valid }}
      onOk={() =>
        onSubmit({
          kind: 'manual_refund',
          amount: Number(amount).toFixed(2),
          currency,
          reference: reference.trim()
        })
      }
      onCancel={onCancel}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="This records a refund. It does not make one."
        description="Refund the customer in Paystack first, then paste its reference here."
      />
      <Form layout="vertical">
        <Form.Item
          label="Amount"
          validateStatus={touched && errors.amount ? 'error' : ''}
          help={touched ? errors.amount : null}
          extra={
            suggested.amount
              ? `Suggested: ${money(suggested.amount, suggested.currency)} — the customer paid ${money(
                (order || {}).priceAmount,
                (order || {}).priceCurrency
              )}.`
              : null
          }
        >
          <Space>
            <InputNumber
              value={amount}
              min={0}
              precision={2}
              style={{ width: 180 }}
              onChange={value => {
                setTouched(true)
                setAmount(value)
              }}
            />
            <Select value={currency} style={{ width: 100 }} onChange={setCurrency}>
              <Select.Option value="GHS">GHS</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
            </Select>
          </Space>
        </Form.Item>
        <Form.Item
          label="Paystack reference"
          validateStatus={touched && errors.reference ? 'error' : ''}
          help={touched ? errors.reference : null}
        >
          <Input
            value={reference}
            placeholder="The reference of the money that actually moved"
            onChange={event => {
              setTouched(true)
              setReference(event.target.value)
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

RefundModal.propTypes = {
  open: PropTypes.bool,
  saving: PropTypes.bool,
  order: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

// Only packages of a compatible identifier kind: a chassis-number package cannot be run against
// an order holding a VIN, and offering it would be a refusal the operator meets after choosing.
export function SwitchProductModal({ open, saving, order, products, onCancel, onSubmit }) {
  const [productKey, setProductKey] = useState(null)
  const current = (order || {}).productKey
  const choices = (products || []).filter(
    product =>
      product.identifierKind === (order || {}).identifierKind && product.key !== current
  )

  return (
    <Modal
      title="Switch this order to another report"
      visible={open}
      confirmLoading={saving}
      okText="Switch and run it"
      okButtonProps={{ disabled: !productKey }}
      onOk={() => onSubmit({ kind: 'switch_product', productKey })}
      onCancel={onCancel}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="This buys another report, and the customer is not charged again."
        description="They keep the price they paid. Use it when the first report found nothing and another source might."
      />
      <Select
        showSearch
        optionFilterProp="children"
        style={{ width: '100%' }}
        placeholder="Pick a package"
        value={productKey}
        onChange={setProductKey}
      >
        {choices.map(product => (
          <Select.Option key={product.key} value={product.key}>
            {product.name}
            {product.active ? '' : ' (not on sale)'}
          </Select.Option>
        ))}
      </Select>
      {choices.length ? null : (
        <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
          There is no other package that takes this kind of number.
        </Typography.Paragraph>
      )}
    </Modal>
  )
}

SwitchProductModal.propTypes = {
  open: PropTypes.bool,
  saving: PropTypes.bool,
  order: PropTypes.object,
  products: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

export function RetryPartButton({ part, saving, onRetry }) {
  // Only a report that produced nothing. Retrying a ready one would charge us twice for a file we
  // already hold.
  const retryable = ['failed', 'no_data', 'cancelled'].includes(part.status)
  if (!retryable) return null
  return (
    <Button
      size="small"
      loading={saving}
      onClick={() =>
        Modal.confirm({
          title: `Buy ${part.label} again?`,
          content: 'This spends money with the supplier. The customer is not charged.',
          okText: 'Buy it again',
          onOk: () => onRetry(part)
        })
      }
    >
      Retry
    </Button>
  )
}

RetryPartButton.propTypes = {
  part: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  onRetry: PropTypes.func.isRequired
}
